import type {
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import type { AbstractControl } from '@angular/forms';
import { FormControl } from '@angular/forms';
import { shakeAnimation } from '@freelancer/animations';
import { RepetitiveSubscription } from '@freelancer/decorators';
import type { Timer } from '@freelancer/time-utils';
import { TimeUtils } from '@freelancer/time-utils';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import type { SelectItem } from '@freelancer/ui/select';
import { TagType } from '@freelancer/ui/tag';
import { FontColor } from '@freelancer/ui/text';
import { dirtyAndValidate } from '@freelancer/ui/validators';
import { isEqual, parseEmailCSVValues } from '@freelancer/utils';
import { Subscription } from 'rxjs';
import { map, pairwise, startWith } from 'rxjs/operators';
import { Focus } from '../focus/focus.service';
import {
  MultiSelectCounterPosition,
  type MultiSelectInputBlurBehaviour,
  MultiSelectItemHighlightColor,
  MultiSelectSize,
} from './multi-select.types';

@Component({
  selector: 'fl-multi-select',
  template: `
    <div
      class="MultiSelectContainer"
      role="combobox"
      [attr.aria-errormessage]="errorId"
      [attr.aria-expanded]="dropdownVisible"
      [attr.aria-haspopup]="'listbox'"
      [attr.aria-owns]="dropdownContainerId"
    >
      <div class="MultiSelectInner">
        <div
          class="InputContainer"
          [class.Disabled]="control.disabled"
          [class.Error]="hasError && !isWarning"
          [class.IsFocused]="isFocused"
          [ngStyle]="{ maxHeight: maxHeight }"
          [@shakeAnimation]="{
            value: shakeState,
            params: {
              duration: 200,
              translateX: '3px',
            },
          }"
          (@shakeAnimation.done)="shakeDone()"
        >
          <fl-tag
            *ngFor="let selection of selectedItems; trackBy: trackBySelectItem"
            role="option"
            aria-selected="true"
            [type]="TagType.DISMISSABLE"
            [flMarginBottom]="'xxsmall'"
            [flMarginRight]="'xxsmall'"
            [isInvalid]="selection.isInvalid"
            (mousedown)="handleMousedown($event)"
            (dismiss)="removeItem(selection)"
            (dblclick)="modifyItem(selection)"
            (touchstart)="handleTouchStart(selection)"
            (touchend)="handleTouchEnd()"
          >
            {{ selection.displayText }}
          </fl-tag>
          <!-- eslint-disable local-rules/template-attribute-names -->
          <input
            #rawInput
            class="RawInput"
            autocomplete="off"
            data-1p-ignore
            data-lpignore="true"
            [ngStyle]="{ width: inputWidth + 'ch' }"
            [attr.aria-controls]="dropdownContainerId"
            [attr.aria-invalid]="
              (control.touched || control.dirty) && control.invalid
            "
            [attr.maxlength]="allowCustomEntry && maxLength ? maxLength : null"
            [class.Hide]="
              (selectedItems.length > 0 && hideInputOnNonEmpty) || disableInput
            "
            [formControl]="inputControl"
            (keyup)="handleInputKeyUp($event)"
            (keydown)="handleInputKeyDown($event)"
            (focus)="handleInputFocus()"
            (blur)="handleInputBlur()"
            placeholder="{{
              placeholder && (!control.value || control.value?.length === 0)
                ? placeholder
                : secondaryPlaceholder
            }}"
          />
          <!-- eslint-enable local-rules/template-attribute-names -->
        </div>
        <div
          *ngIf="dropdownVisible && !disableDropdown"
          class="DropdownContainer"
          role="listbox"
          [attr.id]="dropdownContainerId"
        >
          <fl-scrollable-content
            class="DropdownScrollbar"
            [autoHeight]="true"
            [hideHorizontalScroll]="true"
          >
            <div
              class="DropdownItem"
              *ngFor="
                let option of filteredOptions;
                let i = index;
                let first = first;
                trackBy: trackBySelectItem
              "
              (click)="handleDropdownClick(option)"
              (mousedown)="handleMousedown($event)"
              [class.DarkBlueHighlight]="
                dropdownItemSelectColor ===
                MultiSelectItemHighlightColor.BLUE_DARK
              "
              [class.GrayHighlight]="
                dropdownItemSelectColor === MultiSelectItemHighlightColor.GRAY
              "
              [class.IsDisabled]="option.disabled"
              [class.IsHighlighted]="
                i === highlightIndex && !option.disabled && showHighlight
              "
            >
              <fl-text
                class="DropdownItemText"
                id="{{ option.displayText }}"
                role="option"
                [color]="
                  i === highlightIndex && !option.disabled && showHighlight
                    ? FontColor.LIGHT
                    : textColor
                "
                [class.IsBlueText]="first && showAddingLine && showPlusIcon"
              >
                {{ option.displayText }}
              </fl-text>
              <fl-icon
                *ngIf="first && showAddingLine && showPlusIcon"
                [color]="IconColor.SECONDARY"
                [flMarginRight]="'xxsmall'"
                [name]="'ui-add-circle-outline'"
                [size]="IconSize.MID"
              ></fl-icon>
            </div>
          </fl-scrollable-content>
        </div>
      </div>
      <div class="Footer">
        <fl-text
          *ngIf="
            maxSelections !== undefined &&
            (hasError
              ? counterPosition === MultiSelectCounterPosition.RIGHT
              : true)
          "
          i18n="Counter for selected items"
          [class.Footer-leftSide]="
            counterPosition === MultiSelectCounterPosition.LEFT
          "
          [class.Footer-rightSide]="
            counterPosition === MultiSelectCounterPosition.RIGHT
          "
          [flHide]="
            counterPosition === MultiSelectCounterPosition.LEFT && hasError
          "
          [color]="
            selectionsCount <= maxSelections
              ? hasError && counterPosition === MultiSelectCounterPosition.RIGHT
                ? FontColor.ERROR
                : FontColor.FOREGROUND
              : FontColor.ERROR
          "
        >
          {{ selectionsCount }}/{{ maxSelections }} selected
        </fl-text>
        <fl-validation-error
          *ngIf="
            hasError || counterPosition === MultiSelectCounterPosition.RIGHT
          "
          class="Footer-leftside"
          [id]="errorId"
          [control]="control"
          [isWarning]="isWarning"
        ></fl-validation-error>
      </div>
    </div>
  `,
  styleUrls: ['./multi-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [shakeAnimation],
})
export class MultiSelectComponent<T> implements OnChanges, OnDestroy, OnInit {
  FontColor = FontColor;
  IconColor = IconColor;
  IconSize = IconSize;
  MultiSelectItemHighlightColor = MultiSelectItemHighlightColor;
  MultiSelectCounterPosition = MultiSelectCounterPosition;
  TagType = TagType;

  dropdownContainerId: string;
  errorId: string;

  @HostBinding('class.IsFocused') isFocused = false;
  dropdownVisible = false;
  filteredOptions: SelectItem<string | T>[] = [];
  highlightIndex = 0;
  inputContainerHeight = 46;
  inputWidth = 1;
  inputControl = new FormControl('', { nonNullable: true });
  isWarning = false;
  maxHeight: string;
  selectedItems: SelectItem<string | T>[] = [];
  /** Indicates whether we are displaying the "Adding" line in the dropdown */
  showAddingLine = false;
  showHighlight: boolean;
  shakeState = 'void';
  selectionsCount = 0;

  private longPressTimeout: Timer;

  @RepetitiveSubscription()
  private errorSubscription?: Subscription;
  private inputSubscription?: Subscription;
  private valueChangedSubscription?: Subscription;

  @ViewChild('rawInput') rawInput: ElementRef<HTMLInputElement>;

  /**
   * Allow custom items to be added by the user.
   *
   * Note: `control` must accept `string` type if set to `true`.
   */
  @Input() allowCustomEntry?: T extends string ? boolean : false;
  @Input() control: FormControl<readonly T[]>;

  /**
   * Customize the appearance of the adding line in the dropdown.
   * When enabled, an add icon and the specified text will appear in blue.
   */
  @Input() showPlusIcon = false;

  /**
   * Customize the text displayed for adding options.
   * The default is 'Adding 'text''. When set, it will be displayed as 'displayText 'text''.
   */
  @Input() addingText = $localize`Adding`;

  /**
   * Disable the dropdown from showing.
   * The default is false. When set to true, the dropdown will not show.
   * This option is useful if using a different design to select the options.
   */
  @Input() disableDropdown = false;

  /**
   * Disable the input element from showing.
   * The default is false. When set to true, the input element will not show.
   * This option is useful for preventing the user from inputting while a load is in progress.
   */
  @Input() disableInput = false;

  /**
   * Initially show the highlight effect in the dropdown.
   * If set to false, users can trigger the highlight by navigating using the 'up' or 'down' buttons.
   */
  @Input() showHighlightInitially = true;

  /** Customize the color of a dropdown item when it is selected. */
  @Input() dropdownItemSelectColor: MultiSelectItemHighlightColor =
    MultiSelectItemHighlightColor.BLUE_DARK;

  /**
   * Conditionally hide the input element when there are selected items.
   * If there are already selected items, the input element will be hidden.
   */
  @Input() hideInputOnNonEmpty = false;
  @Input() invalidInputs: readonly string[] = [];
  @Input() openDropdownOnFocus = false;
  @Input() options: readonly SelectItem<T>[] = [];
  @Input() size = MultiSelectSize.MID;
  @Input() textColor: FontColor = FontColor.FOREGROUND;
  @Input() maxSelections?: number;
  @Input() counterPosition?: MultiSelectCounterPosition =
    MultiSelectCounterPosition.LEFT;

  @Input()
  set maxRows(value: number) {
    if (value) {
      this.maxHeight = `${value * this.inputContainerHeight}px`;
    }
  }

  /** default placeholder when there's no entry yet */
  @Input() placeholder?: string;

  /** placeholder when there's already an entry */
  @Input() secondaryPlaceholder = '';

  /**
   * Number of characters you can type in the input.
   * This will only work when used with allowCustomEntry
   */
  @Input() maxLength?: number;

  /** First validation happens only on blur, and validates on keyup once an error exists */
  @Input() dynamicValidation?: boolean = false;

  /**
   * Allow pasting a string separated by `bulkEntrySeparators` to add multiple
   * emails at once. Bulk entry by username is not supported
   */
  @Input() allowBulkEntry = false;

  /**
   * Separators used to parse the string pasted into the input.
   * Use with `allowBulkEntry`.
   */
  @Input() bulkEntrySeparators: readonly string[] = [','];

  /**
   * What the input should do when blurred.
   * - 'clear' empties the field (default)
   * - 'add' adds the current value as a multiselect item
   * - 'none' leaves the text
   */
  @Input() blurBehaviour: MultiSelectInputBlurBehaviour = 'clear';

  @Output() inputChange = new EventEmitter<string>();

  /**
   * This event carries the item that was removed.
   */
  @Output() itemRemoved = new EventEmitter<T | string>();

  get hasError(): boolean {
    return this.control.invalid && (this.control.touched || this.control.dirty);
  }

  private dropdownHideTimeout?: Timer;

  constructor(
    private timeUtils: TimeUtils,
    private changeDetectorRef: ChangeDetectorRef,
    private focus: Focus,
  ) {}

  ngOnInit(): void {
    this.showHighlight = this.showHighlightInitially;
    this.dropdownContainerId = `dropdown_${this.generateUniqueID()}`;
    this.errorId = `error_${this.generateUniqueID()}`;

    this.inputSubscription = this.inputControl.valueChanges.subscribe(value => {
      this.refilterOptions();
      this.fixupHighlightOverflow();
      this.inputChange.emit(value);
    });
    // Set up disabled map to actual input field
    this.control.registerOnDisabledChange(disabled => {
      if (disabled) {
        this.inputControl.disable();
      } else {
        this.inputControl.enable();
      }
    });
    // Bind the invalid control to the class var
    if (this.control.disabled) {
      this.inputControl.disable();
    }

    this.valueChangedSubscription = this.control.valueChanges
      .pipe(
        startWith(this.control.value ?? []),
        pairwise(),
        map(([prev, curr]) => {
          const prevValues = prev ?? [];
          const currValues = curr ?? [];
          return {
            // Determine if the change was an addition or removal of an item
            type:
              prevValues.length === currValues.length
                ? 'none'
                : prevValues.length < currValues.length
                  ? 'addition'
                  : 'removal',
            previousValue: prev,
            currentValue: curr,
          };
        }),
      )
      .subscribe(change => {
        if (change.type === 'addition' && this.blurBehaviour === 'none') {
          // If the blurBehaviour is set to 'none', we want to clear the input field
          // when a new item is added
          this.rawInput.nativeElement.value = '';
        }
        this.getSelectedItems();
      });
  }

  ngOnDestroy(): void {
    if (this.inputSubscription) {
      this.inputSubscription.unsubscribe();
    }
    if (this.errorSubscription) {
      this.errorSubscription.unsubscribe();
    }

    if (this.valueChangedSubscription) {
      this.valueChangedSubscription.unsubscribe();
    }

    if (this.longPressTimeout) {
      clearTimeout(this.longPressTimeout);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('options' in changes) {
      this.getSelectedItems();
    }

    if ('invalidInputs' in changes) {
      this.getSelectedItems();
    }

    if ('secondaryPlaceholder' in changes) {
      // optional chaining is required as the initial onChanges can run
      // before #rawInput viewChild is ready
      this.inputWidth =
        (this.rawInput?.nativeElement.value.length ?? 0) +
        this.secondaryPlaceholder.length +
        1;
    }

    if ('control' in changes || 'dynamicValidation' in changes) {
      if (this.errorSubscription) {
        this.errorSubscription.unsubscribe();
      }
      this.errorSubscription = this.control.statusChanges.subscribe(() => {
        if (this.hasError && !this.isWarning && this.dynamicValidation) {
          this.shakeState = 'active';
        }
        this.changeDetectorRef.markForCheck();
      });

      // This sets the control's updateOn value to `blur` if on dynamicValidation
      if (this.control && this.dynamicValidation) {
        this.control = Object.defineProperty(this.control, 'updateOn', {
          get: () => 'blur',
          configurable: true,
        });
      }

      this.getSelectedItems();
    }
  }

  showDropdown(): void {
    this.dropdownVisible = true;
    // cancel closing the dropdown if we were about to
    if (this.dropdownHideTimeout) {
      clearTimeout(this.dropdownHideTimeout);
      this.dropdownHideTimeout = undefined;
    }
  }

  handleTouchStart(item: SelectItem<string | T>): void {
    if (this.longPressTimeout) {
      clearTimeout(this.longPressTimeout);
    }

    this.longPressTimeout = this.timeUtils.setTimeout(() => {
      this.modifyItem(item);
    }, 500);
  }

  handleTouchEnd(): void {
    if (this.longPressTimeout) {
      clearTimeout(this.longPressTimeout);
    }
  }

  handleInputFocus(): void {
    this.isFocused = !this.isFocused;
    this.highlightIndex = 0;
    this.refilterOptions();
    if (this.openDropdownOnFocus) {
      this.showDropdown();
    }

    if (this.dynamicValidation && this.hasError) {
      this.isWarning = true;
    }
  }

  handleInputBlur(): void {
    this.isFocused = false;
    // hide dropdown after a timeout so clicks still register
    this.dropdownHideTimeout = this.timeUtils.setTimeout(() => {
      if (this.blurBehaviour === 'add') {
        if (this.allowBulkEntry) {
          const bulkEntries = parseEmailCSVValues(
            this.bulkEntrySeparators,
            this.inputControl.value,
          );

          if (bulkEntries.length > 1) {
            bulkEntries.forEach(entry =>
              this.addItem({
                item: { displayText: entry, value: entry },
                validateOnAdd: true,
              }),
            );
          } else {
            this.addItem({ validateOnAdd: true });
          }
        } else {
          this.addItem({ validateOnAdd: true });
        }
      }
      this.highlightIndex = 0;
      this.dropdownVisible = false;
      this.showHighlight = this.showHighlightInitially;
      this.changeDetectorRef.markForCheck();
    }, 250);

    if (this.dynamicValidation) {
      this.getSelectedItems();
      this.validateField();
      this.isWarning = false;
    }

    if (this.hasError) {
      this.shakeState = 'active';
    }
    this.changeDetectorRef.markForCheck();

    switch (this.blurBehaviour) {
      case 'clear':
        this.rawInput.nativeElement.value = '';
        break;
      case 'add':
        // No-op - this has been handled in the timeout
        break;
      case 'none':
        break;
      default:
        throw new Error(`Unknown blurBehaviour: ${this.blurBehaviour}`);
    }
  }

  handleInputKeyDown(e: KeyboardEvent): void {
    // eslint-disable-next-line deprecation/deprecation
    const k = (e.key && e.key.toLowerCase()) || e.keyCode;
    switch (k) {
      case 'backspace':
      case 8: {
        if (this.rawInput.nativeElement.value === '') {
          this.removeItem();
        }
        break;
      }
      /**
       * 'down' is an IE/Edge specific value
       * 40 is a fallback if e.key is undefined
       */
      case 'arrowdown':
      case 'down':
      case 40: {
        // Prevent cursor from moving to the right
        e.preventDefault();
        break;
      }
      /**
       * 'up' is an IE/Edge specific value
       * 38 is a fallback if e.key is undefined
       */
      case 'arrowup':
      case 'up':
      case 38: {
        // Prevent cursor from moving to the right
        e.preventDefault();
        break;
      }
      default:
        break; // Do nothing
    }
    this.inputWidth =
      this.rawInput.nativeElement.value.length +
      this.secondaryPlaceholder.length +
      1;
  }

  handleInputKeyUp(e: KeyboardEvent): false | undefined {
    // eslint-disable-next-line deprecation/deprecation
    const k = (e.key && e.key.toLowerCase()) || e.keyCode;
    // On the second letter onwards make sure dropdown is visible
    if (this.rawInput.nativeElement.value.length >= 1) {
      this.showDropdown();
    }
    // Hide it on escape and delete input value
    switch (k) {
      case 'escape':
      case 27: {
        this.rawInput.nativeElement.value = '';
        this.highlightIndex = 0;
        this.dropdownVisible = false;
        this.showHighlight = this.showHighlightInitially;
        break;
      }
      case 'enter':
      case 13: {
        if (this.dropdownVisible) {
          e.stopPropagation();
        }

        if (this.allowBulkEntry) {
          const bulkEntries = parseEmailCSVValues(
            this.bulkEntrySeparators,
            this.inputControl.value,
          );

          if (bulkEntries.length > 1) {
            bulkEntries.forEach(entry =>
              this.addItem({ item: { displayText: entry, value: entry } }),
            );
          } else {
            this.addItem();
          }
        } else {
          this.addItem();
        }

        break;
      }
      /**
       * 'down' is an IE/Edge specific value
       * 40 is a fallback if e.key is undefined
       */
      case 'arrowdown':
      case 'down':
      case 40: {
        if (!this.dropdownVisible || !this.showHighlight) {
          this.showHighlight = true;
          this.showDropdown();
          this.moveUp();
        }
        this.moveDown();
        e.stopPropagation();
        return false;
      }
      /**
       * 'up' is an IE/Edge specific value
       * 38 is a fallback if e.key is undefined
       */
      case 'arrowup':
      case 'up':
      case 38: {
        if (!this.showHighlight) {
          this.showHighlight = true;
        }

        this.moveUp();
        e.stopPropagation();
        return false;
      }
      default:
        break; // Do nothing
    }
  }

  fixupHighlightOverflow(): void {
    if (this.highlightIndex < 0) {
      this.highlightIndex += this.filteredOptions.length;
    } else if (this.highlightIndex >= this.filteredOptions.length) {
      this.highlightIndex = 0;
    }
  }

  checkForDuplicate(inputValue: string): boolean {
    if (this.control.value) {
      const currentItems = this.control.value.map(controlValue =>
        typeof controlValue === 'string'
          ? controlValue.toLowerCase()
          : controlValue,
      );

      return currentItems.indexOf(inputValue.trim().toLowerCase()) > -1;
    }
    return false;
  }

  addItem({
    item,
    validateOnAdd = false,
  }: {
    item?: SelectItem<string | T>;
    validateOnAdd?: boolean;
  } = {}): void {
    let itemToAdd: SelectItem<string | T> | undefined;
    if (item) {
      // user clicked on an item directly
      itemToAdd = item;
    } else if (this.getItem()) {
      // input is blurred or user pressed enter with an item highlighted
      itemToAdd = this.getItem();
    }

    if (!itemToAdd || itemToAdd.disabled) {
      return;
    }

    const previousValueHasError = this.hasError;
    const previousSelections = this.control.value ?? [];
    const newSelections = this.control.value
      ? [...this.control.value, itemToAdd.value]
      : [itemToAdd.value];

    if (
      this.isAllowCustomEntryControl(this.control) &&
      this.isAllowCustomEntryItems(newSelections)
    ) {
      this.control.setValue(newSelections, { emitEvent: false });
    } else if (this.isNotAllowCustomEntryItems(newSelections)) {
      this.control.setValue(newSelections, { emitEvent: false });
    }

    // Remove item if it is invalid
    if (validateOnAdd && this.control.status === 'INVALID') {
      this.control.setValue(previousSelections, { emitEvent: false });
    }

    this.getSelectedItems();

    if (this.dynamicValidation) {
      this.handleDynamicValidation(previousValueHasError);
    } else {
      dirtyAndValidate(this.control);
    }

    this.highlightIndex = 0;
    this.inputControl.setValue('');
    this.refilterOptions();
    this.dropdownVisible = false;
    this.showHighlight = this.showHighlightInitially;
    this.focus.focusElement(this.rawInput);
  }

  modifyItem(item: SelectItem<string | T>): void {
    if (item.displayText) {
      this.removeItem(item);
      this.inputControl.setValue(item.displayText);
      this.dropdownVisible = true;
    }
  }

  removeItem(item?: SelectItem<string | T>): void {
    let newSelections: (string | T)[];
    let removedItem = item?.value;
    if (item === undefined) {
      newSelections = [...this.control.value];
      removedItem = newSelections.pop();
    } else {
      newSelections = [...this.control.value].filter(
        i => !isEqual(i, item.value),
      );
    }

    if (removedItem) {
      this.itemRemoved.emit(removedItem);
    }

    const previousValueHasError = this.hasError;
    if (
      this.isAllowCustomEntryControl(this.control) &&
      this.isAllowCustomEntryItems(newSelections)
    ) {
      this.control.setValue(newSelections, { emitEvent: false });
    } else if (this.isNotAllowCustomEntryItems(newSelections)) {
      this.control.setValue(newSelections, { emitEvent: false });
    }
    this.getSelectedItems();

    if (this.dynamicValidation) {
      this.handleDynamicValidation(previousValueHasError);
    } else {
      dirtyAndValidate(this.control);
    }

    this.refilterOptions();
    this.dropdownVisible = false;
    this.showHighlight = this.showHighlightInitially;
    this.focus.focusElement(this.rawInput);
  }

  // Scrolls the dropdown menu
  scrollItemIntoView(): void {
    const elements = document.querySelectorAll('.DropdownItem');

    // if elements[this.highlightIndex] exist, scroll to it
    if (elements[this.highlightIndex]) {
      const e = elements[this.highlightIndex] as Element & {
        scrollIntoViewIfNeeded?(): void;
      };

      if (e.scrollIntoViewIfNeeded) {
        e.scrollIntoViewIfNeeded();
      } else {
        e.scrollIntoView();
      }
    }
  }

  // Gets the highlighted item in the dropdown menu
  getItem(): SelectItem<string | T> | undefined {
    return this.dropdownVisible
      ? this.filteredOptions[this.highlightIndex]
      : undefined;
  }

  handleMousedown(event: Event): void {
    // Prevents input blur from being called when clicking on the dropdown options
    // or when dismissing an option
    event.preventDefault();
  }

  handleDropdownClick(item: SelectItem<string | T>): void {
    if (this.allowBulkEntry) {
      const bulkEntries = parseEmailCSVValues(
        this.bulkEntrySeparators,
        String(item.value),
      );

      if (bulkEntries.length > 1) {
        bulkEntries.forEach(entry =>
          this.addItem({ item: { displayText: entry, value: entry } }),
        );
      } else {
        this.addItem({ item });
      }
    } else {
      this.addItem({ item });
    }
  }

  countSelectedItems(): void {
    if (this.maxSelections) {
      this.selectionsCount = this.control.value.length;
    }
  }

  // Sets the fl-tags based on the control to display on the input field
  getSelectedItems(): void {
    if (this.control && this.control.value) {
      this.selectedItems = this.control.value.map(
        value =>
          this.options.find(item => isEqual(item.value, value)) || {
            // Using `String()` as `toString` requires `T extends Object`, but using that causes
            // `T` to get inferred as `any`
            displayText: String(value),
            value,
            isInvalid: this.invalidInputs.includes(String(value)),
          },
      );
      this.countSelectedItems();
    }
  }

  // Filters the dropdown choices based on the input field text
  refilterOptions(): void {
    const rawValue = this.rawInput.nativeElement.value.trim();
    this.showAddingLine = false;
    this.filteredOptions = this.options.filter(option => {
      if (
        // Check if control has values
        (this.control.value &&
          // `allowCustomEntry` - control and options should be all string
          // These type guards should all be true when `allowCustomEntry` is true
          this.isAllowCustomEntryControl(this.control) &&
          this.isAllowCustomEntrySelectItem(option) &&
          this.control.value.includes(option.value)) ||
        // No `allowCustomEntry` - control and options should be T
        // These type guards should all be true when `allowCustomEntry` is falsy
        (this.isNotAllowCustomEntrySelectItem(option) &&
          this.control.value.includes(option.value))
      ) {
        return false;
      }

      // Filter out options doesn't partially or fully match the raw user input
      if (!option.displayText.toLowerCase().includes(rawValue.toLowerCase())) {
        return false;
      }

      return true;
    });
    // only show "Adding '{text}'" if filteredOptions has multiple elements
    // or empty or there is 1 filteredOptions but the displayText !== user's input
    if (
      this.allowCustomEntry &&
      rawValue !== '' &&
      (this.filteredOptions.length !== 1 ||
        (this.filteredOptions.length === 1 &&
          this.filteredOptions[0].displayText.toLowerCase() !==
            rawValue.toLowerCase()))
    ) {
      const duplicateDetected = this.checkForDuplicate(rawValue);

      if (!duplicateDetected) {
        this.showAddingLine = true;
        this.filteredOptions.unshift({
          displayText: `${this.addingText} '${rawValue}'`,
          value: rawValue,
        });
      } else {
        this.filteredOptions.unshift({
          displayText: `'${rawValue}' is already added`,
          value: rawValue,
          disabled: true,
        });
      }
    }
  }

  // Move down the dropdown choices
  moveDown(): void {
    this.highlightIndex += 1;
    this.fixupHighlightOverflow();
    this.scrollItemIntoView();
  }

  // Move up the dropdown choices
  moveUp(): void {
    this.highlightIndex -= 1;
    this.fixupHighlightOverflow();
    this.scrollItemIntoView();
  }

  handleDynamicValidation(hasError: boolean): void {
    // We want to only keep validating if there is already an existing error
    if (!this.dynamicValidation || !hasError) {
      return;
    }

    this.validateField();
  }

  validateField(): void {
    if (this.selectedItems.length > 0) {
      dirtyAndValidate(this.control);
    } else {
      this.control.reset([]);
    }
  }

  trackBySelectItem(_: number, item: SelectItem<string | T>): string | T {
    return item.value;
  }

  shakeDone(): void {
    this.shakeState = 'void';
  }

  isAllowCustomEntryItems(items: (string | T)[]): items is string[] {
    return this.allowCustomEntry === true;
  }

  isNotAllowCustomEntryItems(items: (string | T)[]): items is T[] {
    return !this.allowCustomEntry;
  }

  isAllowCustomEntrySelectItem(
    item: SelectItem<string | T>,
  ): item is SelectItem<string> {
    return this.allowCustomEntry === true;
  }

  isNotAllowCustomEntrySelectItem(
    item: SelectItem<string | T>,
  ): item is SelectItem<T> {
    return !this.allowCustomEntry;
  }

  isAllowCustomEntryControl(
    control: AbstractControl,
  ): control is FormControl<string[]> {
    return this.allowCustomEntry === true;
  }

  private generateUniqueID(): string {
    return Math.random().toString(36).substring(2, 6);
  }
}
